/**
 * Postmaster V2 — Per-Iterable-Campaign FBL spam rate tab.
 *
 * Sortable table of (Date × Domain × Iterable Campaign × Spam-Rate). Rows are
 * cross-DB joined to remote DB14 iterable_campaigns_cache in the route
 * handler — the JOIN is JS-side because LocalDB (postmaster_v2_fbl_spam_rate)
 * and DB14 (iterable_campaigns_cache) live on different MySQL instances.
 * When the campaign isn't in the cache yet, campaign_name is null and the
 * row renders as "Unbekannte Kampagne ({id})".
 *
 * Backed by GET /api/postmaster/v2/fbl (postmaster-v2.ts route), written
 * daily by `postmaster_v2_daily_sync` cron via the two-step FBL pattern:
 *   1. FEEDBACK_LOOP_ID alone → list of FBL IDs for (domain × day)
 *   2. Per ID, FEEDBACK_LOOP_SPAM_RATE filtered by feedback_loop_id
 *
 * Expectation-setting: most days the spam rate is 0 — Google's per-Campaign
 * FBL data only populates when the daily overall spam-rate is non-trivial.
 * The empty-state explains this so the user doesn't read "empty table" as
 * "broken pipeline".
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import moment from 'moment';
import { api } from '../../helpers/apiUtils';

interface FblRow {
  domain: string;
  stat_date: string;
  fbl_id: string;
  iterable_customer_id: string | null;
  iterable_campaign_id: number | null;
  iterable_subaccount_id: string | null;
  spam_rate: number | null;
  campaign_name: string | null;
  campaign_send_at: string | null;
  updated_at: string;
}

// Mirror of V1's spamColor helper (postmaster/index.tsx:71). Google flags
// >0.3% as problematic, >=0.1% worth watching.
function spamColor(v: number | null): string {
  if (v === null || v === undefined) return 'text.disabled';
  if (v >= 0.003) return 'error.main';
  if (v >= 0.001) return 'warning.main';
  return 'success.main';
}

function pct(v: number | null): string {
  if (v === null || v === undefined) return '–';
  return `${(v * 100).toFixed(4)} %`;
}

interface PostmasterV2FblTabProps {
  // null = no filter applied yet (parent still loading the domain list).
  // Empty array = user explicitly selected nothing → render nothing.
  // Populated array = restrict to these domains.
  selectedDomains: string[] | null;
}

const PostmasterV2FblTab: React.FC<PostmasterV2FblTabProps> = ({ selectedDomains }) => {
  const [allRows, setAllRows] = useState<FblRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Last 14 days default; spam-rate-relevant signals tend to be recent.
  const defaultEnd = useMemo(() => moment().format('YYYY-MM-DD'), []);
  const defaultStart = useMemo(() => moment().subtract(14, 'days').format('YYYY-MM-DD'), []);
  const [startDate, setStartDate] = useState<string>(defaultStart);
  const [endDate, setEndDate] = useState<string>(defaultEnd);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/postmaster/v2/fbl', {
          params: { startDate, endDate },
        });
        if (cancelled) return;
        const data: FblRow[] = res.data.data || [];
        setAllRows(data);
      } catch (err) {
        if (!cancelled) setError(`Konnte FBL-Daten nicht laden: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const filteredRows = useMemo(() => {
    if (selectedDomains === null) return allRows;
    const set = new Set(selectedDomains);
    return allRows.filter((r) => set.has(r.domain));
  }, [allRows, selectedDomains]);

  // Sort: worst (highest spam) first, then most recent date.
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const aRate = a.spam_rate ?? -1;
      const bRate = b.spam_rate ?? -1;
      if (bRate !== aRate) return bRate - aRate;
      return b.stat_date.localeCompare(a.stat_date);
    });
  }, [filteredRows]);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            label="Von"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Bis"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : sortedRows.length === 0 ? (
        <Alert severity="info">
          <strong>Keine FBL-Daten für den gewählten Zeitraum.</strong> Per-Campaign-Komplaints
          werden nur sichtbar, wenn die Tages-Spam-Rate &gt; 0 ist (Gmail Volume-Threshold). An
          ruhigen Tagen liefert Google keine FBL-Identifiers — das ist normal, kein Pipeline-Bruch.
        </Alert>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TableContainer sx={{ backgroundColor: '#ffffff', maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    'Datum',
                    'Domain',
                    'Kampagne',
                    'Iterable Campaign ID',
                    'Subaccount',
                    'Spam-Rate',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((r, idx) => (
                  <TableRow key={`${r.domain}-${r.stat_date}-${r.fbl_id}-${idx}`}>
                    <TableCell>{r.stat_date}</TableCell>
                    <TableCell>{r.domain}</TableCell>
                    <TableCell>
                      {r.campaign_name ? (
                        <Typography variant="body2">{r.campaign_name}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Unbekannte Kampagne ({r.iterable_campaign_id ?? r.fbl_id})
                        </Typography>
                      )}
                      {r.campaign_send_at && (
                        <Typography variant="caption" color="text.secondary">
                          {moment(r.campaign_send_at).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.iterable_campaign_id !== null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                          >
                            {r.iterable_campaign_id}
                          </Typography>
                          <Tooltip title="ID in Zwischenablage kopieren">
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigator.clipboard?.writeText(String(r.iterable_campaign_id))
                              }
                            >
                              <ContentCopy sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          –
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                      >
                        {r.iterable_subaccount_id ?? '–'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: spamColor(r.spam_rate), fontWeight: 600 }}>
                      {pct(r.spam_rate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Sortierung: Spam-Rate absteigend, dann Datum absteigend. Zeilen mit{' '}
            <em>Unbekannte Kampagne</em> = Iterable-Campaign-Cache (DB14) hat den Eintrag noch nicht
            (FTWP-Sync-Lag) — Campaign-ID kopierbar.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PostmasterV2FblTab;
